import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Calendar, Download, Banknote, AlertTriangle, ChevronDown, ChevronRight, RefreshCw,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Spinner, EmptyState, Input, Select } from "@/components/ui";
import { addDays, differenceInCalendarDays, parseISO, format } from "date-fns";
import { fmtMoney, fmtDate } from "@/lib/dates";

// =============================================================================
// PayrollSummaryScreen — /hr/payroll/summary
// T-274a — director (or payroll_access) payroll summary. Aggregates APPROVED
// timesheet entries between two dates into per-employee totals matching Xero
// Payroll's earnings lines (Ordinary, OT 1.5x, OT 2x, Leave, LAFHA nights).
// Stays manual-paste for now — full Xero PayRun push is T-274.
//
// 5 Oct 2026 (mig 420 — the Larry 71.6 bug). The split columns on a row are
// written by the database and re-split for the whole day on every change; this
// screen never does award maths. What it does do now:
//   · runs app.timesheet_split_check for the period before anything else and
//     shows a red card if any stored split disagrees with the day rule, with a
//     one-click Recompute (app.recompute_timesheet_split, director-only);
//   · every name expands to its day-by-day lines — date, what (project / depot
//     / leave / night), hours, Ord / 1.5x / 2x — so a total can be checked by
//     eye against the Team week in seconds;
//   · paid leave (mig 383/384 rows, leave_type_code set) is its own column and
//     CSV field. In Xero leave goes on the leave pay item, not Ordinary, so
//     Ordinary here is WORKED ordinary hours only.
// =============================================================================

interface EntryRow {
  entry_id:     string;
  work_date:    string;
  project_id:   string | null;
  start_time:   string | null;
  total_hours:  number | null;
  hours_std:    number;
  hours_th:     number;
  hours_dt:     number;
  lafha_nights: number;
  travel_km:    number | null;
  travel_hours: number | null;
  leave_type_code: string | null;   // mig 383 — an approved leave day written by the leave approval
  is_night:     boolean | null;     // mig 350 — all double time
  ot_suppressed: boolean | null;    // mig 420 — director booked ordinary only (mirror / fill)
  notes:        string | null;
  projects:     { project_code: string | null } | null;
  employees: {
    employee_id:     string;
    first_name:      string | null;
    last_name:       string | null;
    lafha_eligible:  boolean | null;
    employment_type: string | null;   // T-367 — 'director' rows are salaried; excluded from the hourly export
  } | null;
}

interface SummaryRow {
  employee_id:         string;
  name:                string;
  ordinary_hours:      number;   // WORKED ordinary hours (leave excluded — it has its own line)
  ot_1_5x_hours:       number;
  ot_2x_hours:         number;
  leave_hours:         number;   // paid leave hours (mig 383/384 rows) — Xero leave pay item
  total_hours:         number;   // everything approved, for the cross-check
  lafha_nights:        number;
  days:                EntryRow[];  // the lines behind the numbers, for the expand
  lafha_eligible:      boolean;
  onsite_worked_hours: number;   // hours on a project (project_id set) with real hours
  onsite_days:         number;   // distinct on-site working days
  travel_km:           number;
  travel_hours:        number;
  flag_missing_lafha:  boolean;  // eligible + worked away + zero nights claimed
}

// Rows the pre-export check may raise. Warn-only — never blocks the export.
interface PreCheck {
  kind:    "missing_lafha" | "pending";
  message: string;
}

function isoDate(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

// Sums of 2-dp numerics in JS drift (71.599999…). Round once at the edge.
function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

// What a line was, in the team's words.
function lineLabel(e: EntryRow): string {
  if (e.leave_type_code) return `Leave — ${e.leave_type_code}`;
  if (e.project_id) return e.projects?.project_code ?? "Project";
  return "Depot";
}

// mig 420 — a row whose stored split disagrees with the day rule.
interface SplitCheckRow {
  entry_id: string; employee_id: string; work_date: string; total_hours: number;
  stored_std: number; stored_th: number; stored_dt: number;
  should_std: number; should_th: number; should_dt: number;
}

// T-323 — James's pay cycle is a fortnight of two WHOLE Mon→Sun weeks. This
// anchor is a known fortnight-START Monday; every pay fortnight is
// anchor + 14·n days. The buttons + default below derive from it. This does
// NOT force the cycle — it only pre-fills the date boxes; the range stays
// fully editable. Change this one value only if the cycle ever shifts.
const FORTNIGHT_ANCHOR = "2026-06-15";

// Fortnight-start (anchor-aligned Monday) that contains a given date.
function fortnightStartContaining(d: Date): Date {
  const n = Math.floor(differenceInCalendarDays(d, parseISO(FORTNIGHT_ANCHOR)) / 14);
  return addDays(parseISO(FORTNIGHT_ANCHOR), n * 14);
}
function fortnightRangeFrom(startMon: Date): { start: string; end: string } {
  return { start: isoDate(startMon), end: isoDate(addDays(startMon, 13)) };
}

// T-467 — recent fortnights (most recent first) for the jump-to dropdown. i=0
// is the in-progress fortnight; i=1 is the most-recently-completed one (the
// screen's default landing). Data-independent — derived purely from the anchor.
function recentFortnights(count: number): { start: string; end: string; label: string }[] {
  const current = fortnightStartContaining(new Date());
  const out: { start: string; end: string; label: string }[] = [];
  for (let i = 0; i < count; i++) {
    const st = addDays(current, -i * 14);
    const en = addDays(st, 13);
    out.push({
      start: isoDate(st),
      end:   isoDate(en),
      label: `${format(st, "d MMM")} – ${format(en, "d MMM yyyy")}`,
    });
  }
  return out;
}

// Default = the most-recently-COMPLETED fortnight. Payroll is run the Monday
// after a fortnight ends, so opening the screen lands on the correct two weeks
// instead of a rolling "last 14 days from today" window (the off-by-a-week
// trap that put the 16th–29th instead of the 15th–28th).
function defaultDates(): { start: string; end: string } {
  const containing = fortnightStartContaining(new Date());
  return fortnightRangeFrom(addDays(containing, -14));
}

export default function PayrollSummaryScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isDirector = hasRole(user, "director");
  const canView = isDirector || !!user?.payroll_access;

  const { start: defStart, end: defEnd } = defaultDates();
  const [start, setStart] = useState(defStart);
  const [end, setEnd]     = useState(defEnd);

  // T-323 — fortnight nav. Buttons only pre-fill the date boxes; the manual
  // pickers below still override freely. The label tells you whether the
  // current range is a clean anchor-aligned fortnight or an ad-hoc range.
  const fortnightLabel = useMemo(() => {
    const s = parseISO(start);
    const aligned = isoDate(fortnightStartContaining(s)) === start
      && differenceInCalendarDays(parseISO(end), s) === 13;
    return aligned ? `${format(s, "d MMM")} – ${format(parseISO(end), "d MMM yyyy")}` : null;
  }, [start, end]);

  function setFortnight(startMon: Date) {
    const r = fortnightRangeFrom(startMon);
    setStart(r.start); setEnd(r.end);
  }
  function shiftFortnight(deltaFortnights: number) {
    const aligned = fortnightStartContaining(parseISO(start));
    setFortnight(addDays(aligned, deltaFortnights * 14));
  }
  function thisFortnight() {
    setFortnight(parseISO(defaultDates().start));
  }

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["payroll_summary", start, end],
    enabled: !!canView && !!start && !!end,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("timesheet_entries")
        .select(`
          entry_id, work_date, project_id, start_time, total_hours, hours_std, hours_th, hours_dt,
          lafha_nights, travel_km, travel_hours, leave_type_code, is_night, ot_suppressed, notes,
          projects:project_id ( project_code ),
          employees:employee_id ( employee_id, first_name, last_name, lafha_eligible, employment_type )
        `)
        .gte("work_date", start)
        .lte("work_date", end)
        .not("approved_at", "is", null)
        .order("work_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as EntryRow[];
    },
  });

  // mig 420 — the stored split of every row in the range vs the day rule.
  // Empty = the numbers below can be trusted. Anything here means a row was
  // left holding a split from before a sibling changed; Recompute fixes it.
  const { data: splitProblems = [], isLoading: checkLoading } = useQuery<SplitCheckRow[]>({
    queryKey: ["payroll_split_check", start, end],
    enabled: !!canView && !!start && !!end,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema("app").rpc("timesheet_split_check", { p_from: start, p_to: end });
      if (error) throw error;
      return (data ?? []) as SplitCheckRow[];
    },
  });
  const recompute = useMutation({
    mutationFn: async () => {
      const { data, error } = await (supabase as any)
        .schema("app").rpc("recompute_timesheet_split", { p_from: start, p_to: end });
      if (error) throw error;
      return Number(data) || 0;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll_split_check", start, end] });
      qc.invalidateQueries({ queryKey: ["payroll_summary", start, end] });
    },
    onError: (e: any) => window.alert(e?.message ?? "Could not recompute the split."),
  });

  // Completeness precheck — entries in the range NOT yet approved. These won't
  // be in the export, so the director should know they're still outstanding.
  const { data: pendingEntries = [] } = useQuery<any[]>({
    queryKey: ["payroll_pending", start, end],
    enabled: !!canView && !!start && !!end,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("timesheet_entries")
        .select("employee_id, employees:employee_id(first_name,last_name)")
        .gte("work_date", start)
        .lte("work_date", end)
        .is("approved_at", null);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // T-467 — recent fortnights for the jump dropdown + full recorded pay-run
  // history (used to mark recorded fortnights and to show the history panel).
  const fortnightOptions = useMemo(() => recentFortnights(14), []);
  const { data: history = [] } = useQuery({
    queryKey: ["payroll_history"],
    enabled: !!canView,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pay_runs")
        .select("pay_run_id, pay_group, paid_date, total_gross, total_super, total_net, period:period_id!inner(period_start)")
        .order("paid_date", { ascending: false })
        .limit(300);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });
  const recordedStarts = useMemo(
    () => new Set((history as any[]).map(r => r.period?.period_start).filter(Boolean)),
    [history]
  );
  const historyByFortnight = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const r of history as any[]) {
      const ps = r.period?.period_start;
      if (!ps) continue;
      if (!map.has(ps)) map.set(ps, []);
      map.get(ps)!.push(r);
    }
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([ps, rows]) => {
        const wages = rows.reduce((acc, r) => acc + (Number(r.total_gross) || 0), 0);
        const zuper = rows.reduce((acc, r) => acc + (Number(r.total_super) || 0), 0);
        const en = isoDate(addDays(parseISO(ps), 13));
        return {
          start: ps,
          label: `${format(parseISO(ps), "d MMM")} – ${format(parseISO(en), "d MMM yyyy")}`,
          groups: rows.map(r => r.pay_group).filter(Boolean),
          wages, zuper, cost: wages + zuper,
          count: rows.length,
        };
      });
  }, [history]);

  const summary: SummaryRow[] = useMemo(() => {
    const map = new Map<string, SummaryRow>();
    const onsiteDates = new Map<string, Set<string>>();  // employee_id → distinct on-site work_dates
    for (const e of entries) {
      if (!e.employees) continue;
      // T-367 — only hourly-paid internal staff belong in the hourly Xero export.
      //  · directors → salaried (their hours still cost to jobs, but pay is gated manually)
      //  · subcontractor / labour_hire → invoice their labour via AP, never payroll
      // All still show in reporting (all-hours man-hour extracts); just not here.
      if (["director", "subcontractor", "labour_hire"].includes(e.employees.employment_type ?? "")) continue;
      const id   = e.employees.employee_id;
      const name = `${e.employees.first_name ?? ""} ${e.employees.last_name ?? ""}`.trim() || "Unnamed";
      const existing = map.get(id) ?? {
        employee_id:         id,
        name,
        ordinary_hours:      0,
        ot_1_5x_hours:       0,
        ot_2x_hours:         0,
        leave_hours:         0,
        total_hours:         0,
        lafha_nights:        0,
        days:                [],
        lafha_eligible:      !!e.employees.lafha_eligible,
        onsite_worked_hours: 0,
        onsite_days:         0,
        travel_km:           0,
        travel_hours:        0,
        flag_missing_lafha:  false,
      };
      const hours = Number(e.total_hours) || 0;
      existing.total_hours += hours;
      existing.days.push(e);
      if (e.leave_type_code) {
        // mig 383/384 — paid leave is its own Xero pay item, never Ordinary.
        existing.leave_hours += hours;
      } else {
        existing.ordinary_hours += Number(e.hours_std) || 0;
        existing.ot_1_5x_hours  += Number(e.hours_th)  || 0;
        existing.ot_2x_hours    += Number(e.hours_dt)  || 0;
      }
      existing.lafha_nights   += Number(e.lafha_nights) || 0;
      existing.travel_km      += Number(e.travel_km)    || 0;
      existing.travel_hours   += Number(e.travel_hours) || 0;
      // On-site = tied to a project AND real hours worked (excludes depot days
      // and allowance-only zero-hour nights, which legitimately need no check).
      if (e.project_id && hours > 0) {
        existing.onsite_worked_hours += hours;
        if (!onsiteDates.has(id)) onsiteDates.set(id, new Set());
        onsiteDates.get(id)!.add(e.work_date);
      }
      map.set(id, existing);
    }
    // Finalise derived fields.
    for (const row of map.values()) {
      row.ordinary_hours = r2(row.ordinary_hours);
      row.ot_1_5x_hours  = r2(row.ot_1_5x_hours);
      row.ot_2x_hours    = r2(row.ot_2x_hours);
      row.leave_hours    = r2(row.leave_hours);
      row.total_hours    = r2(row.total_hours);
      row.travel_km      = r2(row.travel_km);
      row.travel_hours   = r2(row.travel_hours);
      row.days.sort((a, b) => (a.work_date < b.work_date ? -1 : a.work_date > b.work_date ? 1 : (a.start_time ?? "99") < (b.start_time ?? "99") ? -1 : 1));
      row.onsite_days = onsiteDates.get(row.employee_id)?.size ?? 0;
      // Worked away (on-site, real hours) but claimed no LAFHA night — likely a
      // missed allowance. Only flag LAFHA-eligible people (day-trips still get
      // flagged as a prompt to double-check; warn-only, never blocks).
      row.flag_missing_lafha =
        row.lafha_eligible && row.onsite_worked_hours > 0 && row.lafha_nights === 0;
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  // Ad-hoc de-select: employees the director un-ticks are dropped from the
  // export + totals (e.g. labour-hire who slipped through, or a one-off skip).
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const included = useMemo(() => summary.filter(r => !excluded.has(r.employee_id)), [summary, excluded]);
  function toggleExcluded(id: string) {
    setExcluded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  // mig 420 — which names are opened to their day lines.
  const [open, setOpen] = useState<Set<string>>(new Set());
  function toggleOpen(id: string) {
    setOpen(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  const allOpen = summary.length > 0 && summary.every(r => open.has(r.employee_id));

  // T-367 — who's held out of the hourly export, shown so it's transparent
  // (never a silent drop). Directors are salaried; subbies/labour-hire invoice.
  const excludedFromPayroll = useMemo(() => {
    const salaried = new Set<string>();
    const invoiced = new Set<string>();
    for (const e of entries) {
      if (!e.employees) continue;
      const nm = `${e.employees.first_name ?? ""} ${e.employees.last_name ?? ""}`.trim() || "Unnamed";
      const t = e.employees.employment_type;
      if (t === "director") salaried.add(nm);
      else if (t === "subcontractor" || t === "labour_hire") invoiced.add(nm);
    }
    return { salaried: Array.from(salaried), invoiced: Array.from(invoiced) };
  }, [entries]);

  // Warn-only pre-export checks. Advise, don't block.
  const checks: PreCheck[] = useMemo(() => {
    const out: PreCheck[] = [];
    const missing = summary.filter(r => r.flag_missing_lafha);
    if (missing.length > 0) {
      out.push({
        kind: "missing_lafha",
        message: `${missing.length} ${missing.length === 1 ? "person" : "people"} worked on-site with no LAFHA night claimed — check they weren't staying away: ${missing.map(r => r.name).join(", ")}.`,
      });
    }
    if (pendingEntries.length > 0) {
      const names = Array.from(new Set(
        pendingEntries.map(p => `${p.employees?.first_name ?? ""} ${p.employees?.last_name ?? ""}`.trim()).filter(Boolean)
      ));
      out.push({
        kind: "pending",
        message: `${pendingEntries.length} entr${pendingEntries.length === 1 ? "y" : "ies"} in this range ${pendingEntries.length === 1 ? "is" : "are"} not approved yet — they won't be in this export${names.length ? `: ${names.join(", ")}` : ""}.`,
      });
    }
    return out;
  }, [summary, pendingEntries]);

  function downloadCsv() {
    const header = ["Name", "Ordinary Hours", "OT 1.5x Hours", "OT 2x Hours", "Leave Hours", "LAFHA Nights", "Total Hours"];
    const rows = included.map(r => [
      r.name,
      r.ordinary_hours.toFixed(2),
      r.ot_1_5x_hours.toFixed(2),
      r.ot_2x_hours.toFixed(2),
      r.leave_hours.toFixed(2),
      String(r.lafha_nights),
      r.total_hours.toFixed(2),
    ]);
    const csv = [header, ...rows]
      .map(line => line.map(cell => /[,"\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payroll_summary_${start}_to_${end}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  if (!user) return null;
  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <EmptyState
            title="Payroll access required"
            description="Payroll is restricted to directors and users granted payroll access. Ask a director to enable it in Admin > Users."
            icon={AlertTriangle}
          />
        </Card>
      </div>
    );
  }

  const totalEmployees = included.length;
  const totalOrdinary  = r2(included.reduce((acc, r) => acc + r.ordinary_hours, 0));
  const totalOt15x     = r2(included.reduce((acc, r) => acc + r.ot_1_5x_hours, 0));
  const totalOt2x      = r2(included.reduce((acc, r) => acc + r.ot_2x_hours, 0));
  const totalLeave     = r2(included.reduce((acc, r) => acc + r.leave_hours, 0));
  const totalNights    = included.reduce((acc, r) => acc + r.lafha_nights, 0);
  const splitOk        = !checkLoading && splitProblems.length === 0;

  return (
    <div className="p-6 max-w-6xl mx-auto pb-12">
      <button
        onClick={() => navigate("/hr")}
        className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
        type="button"
      >
        <ArrowLeft size={14} /> HR portal
      </button>

      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="text-[22px] font-semibold text-text flex items-center gap-2">
            <Banknote size={22} className="text-accent" /> Payroll
          </h1>
          <p className="text-[13px] text-text-muted mt-1">
            Approved timesheet hours per employee, ready to paste into Xero Payroll. Ordinary is worked hours only — leave has its own column and goes on the leave pay item.
          </p>
          {(excludedFromPayroll.salaried.length > 0 || excludedFromPayroll.invoiced.length > 0) && (
            <p className="text-[12px] text-text-dim mt-1.5">
              Not in this hourly export (still costed + reportable):
              {excludedFromPayroll.salaried.length > 0 && <> salaried — {excludedFromPayroll.salaried.join(", ")};</>}
              {excludedFromPayroll.invoiced.length > 0 && <> invoiced labour — {excludedFromPayroll.invoiced.join(", ")};</>}
            </p>
          )}
        </div>
        {summary.length > 0 && (
          <Button icon={Download} onClick={downloadCsv}>
            Export CSV
          </Button>
        )}
      </div>

      {/* Pay period picker */}
      <Card className="mb-4">
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={14} className="text-text-muted" />
            <span className="text-[13px] font-medium">Pay period</span>
          </div>

          {/* T-323 — one-click fortnight selection (keeps the manual range below) */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Button variant="secondary" size="sm" onClick={() => shiftFortnight(-1)} type="button">◀ Prev</Button>
            <Button variant="secondary" size="sm" onClick={thisFortnight} type="button">This pay fortnight</Button>
            <Button variant="secondary" size="sm" onClick={() => shiftFortnight(1)} type="button">Next ▶</Button>
            <div className="w-60" title="Jump straight to a fortnight">
              <Select
                value={fortnightOptions.some(o => o.start === start) ? start : ""}
                onChange={e => { if (e.target.value) setFortnight(parseISO(e.target.value)); }}
              >
                {!fortnightOptions.some(o => o.start === start) && <option value="">Custom range…</option>}
                {fortnightOptions.map((o, i) => (
                  <option key={o.start} value={o.start}>
                    {o.label}{i === 0 ? " (current)" : ""}{recordedStarts.has(o.start) ? " ✓ recorded" : ""}
                  </option>
                ))}
              </Select>
            </div>
            <span className="text-[12px] font-mono ml-1">
              {fortnightLabel
                ? <span className="text-accent">Fortnight: {fortnightLabel}</span>
                : <span className="text-warn">Custom range — not a whole fortnight</span>}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 max-w-md">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-text-muted">Start</span>
              <input
                type="date"
                value={start}
                onChange={e => setStart(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded border border-line bg-ink-card text-text font-mono text-[13px]"
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-text-muted">End</span>
              <input
                type="date"
                value={end}
                onChange={e => setEnd(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded border border-line bg-ink-card text-text font-mono text-[13px]"
              />
            </label>
          </div>
          <p className="text-[11px] text-text-muted mt-2">
            Only entries with status approved (via <span className="font-mono">/hr/timesheet-approvals</span>) count.
          </p>
        </div>
      </Card>

      <PayRunRecorder start={start} label={fortnightLabel} />

      {/* mig 420 — the hour split must agree with the day rule before anything is pasted into Xero */}
      {!checkLoading && splitProblems.length > 0 && (
        <Card className="mb-4 border-danger/50">
          <div className="p-4 flex items-start gap-3">
            <AlertTriangle size={16} className="text-danger flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-danger">
                {splitProblems.length} entr{splitProblems.length === 1 ? "y" : "ies"} in this period {splitProblems.length === 1 ? "has" : "have"} an hour split that doesn't match the day rule — don't post until this is fixed.
              </div>
              <div className="text-[12px] text-text-muted mt-1.5">
                A row keeps the ordinary / 1.5x / 2x split it was given when it was written. If another entry on the same day was later removed, moved or changed, the row can be left with a stale split. Recompute re-splits every day in the range from scratch; the totals below refresh.
              </div>
              <ul className="text-[11px] font-mono text-text-muted mt-2 space-y-0.5 max-h-32 overflow-auto">
                {splitProblems.slice(0, 20).map(p => (
                  <li key={p.entry_id}>
                    {fmtDate(p.work_date)} · {Number(p.total_hours).toFixed(2)} h · stored {Number(p.stored_std).toFixed(2)} / {Number(p.stored_th).toFixed(2)} / {Number(p.stored_dt).toFixed(2)} → should be {Number(p.should_std).toFixed(2)} / {Number(p.should_th).toFixed(2)} / {Number(p.should_dt).toFixed(2)}
                  </li>
                ))}
                {splitProblems.length > 20 && <li>… and {splitProblems.length - 20} more</li>}
              </ul>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                {isDirector ? (
                  <Button variant="danger" size="sm" icon={RefreshCw} disabled={recompute.isPending} onClick={() => recompute.mutate()} type="button">
                    {recompute.isPending ? "Recomputing…" : "Recompute this period"}
                  </Button>
                ) : (
                  <span className="text-[12px] text-text-muted">Ask a director to open this page and press Recompute.</span>
                )}
                {recompute.isSuccess && <span className="text-[12px] text-ok">Re-split {recompute.data} row{recompute.data === 1 ? "" : "s"}.</span>}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Pre-export checks (warn-only — never blocks the export) */}
      {!isLoading && summary.length > 0 && (
        checks.length > 0 ? (
          <Card className="mb-4 border-warn/40">
            <div className="p-4 flex items-start gap-3">
              <AlertTriangle size={16} className="text-warn flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-warn">Before you export — worth a look</div>
                <ul className="text-[12px] text-text-muted mt-1.5 space-y-1">
                  {checks.map((c, i) => <li key={i}>• {c.message}</li>)}
                </ul>
                <div className="text-[11px] text-text-dim mt-2">These are prompts, not blockers — the export still runs as-is.</div>
              </div>
            </div>
          </Card>
        ) : (
          <div className="mb-4 text-[12px] text-ok flex items-center gap-1.5">
            <span>✓ Pre-export checks passed — no missed LAFHA nights or unapproved entries in this range{splitOk ? ", every hour split agrees with the day rule" : ""}.</span>
          </div>
        )
      )}

      {/* Totals KPIs */}
      {summary.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
          <Kpi label="Employees"     value={totalEmployees} />
          <Kpi label="Ordinary hrs"  value={totalOrdinary.toFixed(2)} />
          <Kpi label="OT 1.5x hrs"   value={totalOt15x.toFixed(2)} tone="warn" />
          <Kpi label="OT 2x hrs"     value={totalOt2x.toFixed(2)}  tone="danger" />
          <Kpi label="Leave hrs"     value={totalLeave.toFixed(2)} />
          <Kpi label="LAFHA nights"  value={totalNights} />
        </div>
      )}

      {/* Per-employee table */}
      <Card>
        <div className="p-5">
          {isLoading ? (
            <div className="py-8 flex justify-center"><Spinner /></div>
          ) : summary.length === 0 ? (
            <EmptyState
              title="No approved entries in this period"
              description="Either nobody's logged hours in this window yet, or you haven't approved them via /hr/timesheet-approvals."
              icon={Banknote}
            />
          ) : (
            <>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <span className="text-[12px] text-text-muted">Click a name to see the days behind the numbers.</span>
              <button type="button" className="text-[12px] text-accent hover:underline"
                onClick={() => setOpen(allOpen ? new Set() : new Set(summary.map(r => r.employee_id)))}>
                {allOpen ? "Collapse all" : "Expand all"}
              </button>
            </div>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-text-muted border-b border-line">
                  <th className="py-2 pl-1 pr-2 w-6" title="Untick to drop from the export"></th>
                  <th className="text-left  py-2 pr-3">Name</th>
                  <th className="text-right py-2 px-3 font-mono">Ord hrs</th>
                  <th className="text-right py-2 px-3 font-mono">OT 1.5x</th>
                  <th className="text-right py-2 px-3 font-mono">OT 2x</th>
                  <th className="text-right py-2 px-3 font-mono">Leave</th>
                  <th className="text-right py-2 px-3 font-mono">Total</th>
                  <th className="text-right py-2 px-3 font-mono">On-site days</th>
                  <th className="text-right py-2 px-3 font-mono">LAFHA nights</th>
                  <th className="text-right py-2 pl-3 font-mono">Travel</th>
                </tr>
              </thead>
              <tbody>
                {summary.map(r => {
                  const isExcluded = excluded.has(r.employee_id);
                  const isOpen = open.has(r.employee_id);
                  return (
                  <React.Fragment key={r.employee_id}>
                  <tr className={`border-b border-line/40 hover:bg-ink-hover ${r.flag_missing_lafha ? "bg-warn/5" : ""} ${isExcluded ? "opacity-40" : ""}`}>
                    <td className="py-2 pl-1 pr-2 text-center">
                      <input type="checkbox" checked={!isExcluded} onChange={() => toggleExcluded(r.employee_id)} className="accent-accent" title="In the export" />
                    </td>
                    <td className="py-2 pr-3 text-text">
                      <button type="button" onClick={() => toggleOpen(r.employee_id)} className="inline-flex items-center gap-1 hover:text-accent" title="Show the days">
                        {isOpen ? <ChevronDown size={13} className="text-text-muted" /> : <ChevronRight size={13} className="text-text-muted" />}
                        {r.name}
                      </button>
                      {r.flag_missing_lafha && (
                        <span title="Worked on-site but no LAFHA night claimed — check they weren't staying away."
                          className="ml-2 inline-flex items-center gap-1 text-[10px] text-warn">
                          <AlertTriangle size={10} /> check LAFHA
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right font-mono">{r.ordinary_hours.toFixed(2)}</td>
                    <td className="py-2 px-3 text-right font-mono text-warn">{r.ot_1_5x_hours.toFixed(2)}</td>
                    <td className="py-2 px-3 text-right font-mono text-danger">{r.ot_2x_hours.toFixed(2)}</td>
                    <td className="py-2 px-3 text-right font-mono text-text-muted">{r.leave_hours > 0 ? r.leave_hours.toFixed(2) : "—"}</td>
                    <td className="py-2 px-3 text-right font-mono text-text-muted">{r.total_hours.toFixed(2)}</td>
                    <td className="py-2 px-3 text-right font-mono text-text-muted">{r.onsite_days || "—"}</td>
                    <td className={`py-2 px-3 text-right font-mono ${r.flag_missing_lafha ? "text-warn" : ""}`}>{r.lafha_nights}</td>
                    <td className="py-2 pl-3 text-right font-mono text-text-muted">
                      {r.travel_km > 0 || r.travel_hours > 0
                        ? `${r.travel_km ? `${r.travel_km.toFixed(0)}km` : ""}${r.travel_km && r.travel_hours ? " · " : ""}${r.travel_hours ? `${r.travel_hours.toFixed(1)}h` : ""}`
                        : "—"}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-line/40 bg-ink-card/40">
                      <td></td>
                      <td colSpan={9} className="py-2 pr-3">
                        <table className="w-full text-[12px]">
                          <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-text-dim">
                              <th className="text-left py-1 pr-3">Day</th>
                              <th className="text-left py-1 pr-3">What</th>
                              <th className="text-right py-1 px-3 font-mono">Hours</th>
                              <th className="text-right py-1 px-3 font-mono">Ord</th>
                              <th className="text-right py-1 px-3 font-mono">1.5x</th>
                              <th className="text-right py-1 px-3 font-mono">2x</th>
                              <th className="text-left py-1 pl-3">Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.days.map(e => {
                              const isLeave = !!e.leave_type_code;
                              return (
                                <tr key={e.entry_id} className="border-t border-line/30">
                                  <td className="py-1 pr-3 font-mono text-text-muted whitespace-nowrap">{format(parseISO(e.work_date), "EEE d MMM")}</td>
                                  <td className="py-1 pr-3 text-text">
                                    {lineLabel(e)}
                                    {e.is_night && <span className="ml-1.5 text-[10px] text-danger">night</span>}
                                    {e.ot_suppressed && <span className="ml-1.5 text-[10px] text-text-dim">ordinary only</span>}
                                  </td>
                                  <td className="py-1 px-3 text-right font-mono">{(Number(e.total_hours) || 0).toFixed(2)}</td>
                                  <td className="py-1 px-3 text-right font-mono text-text-muted">{isLeave ? "—" : (Number(e.hours_std) || 0).toFixed(2)}</td>
                                  <td className="py-1 px-3 text-right font-mono text-warn">{isLeave || !(Number(e.hours_th) > 0) ? "—" : Number(e.hours_th).toFixed(2)}</td>
                                  <td className="py-1 px-3 text-right font-mono text-danger">{isLeave || !(Number(e.hours_dt) > 0) ? "—" : Number(e.hours_dt).toFixed(2)}</td>
                                  <td className="py-1 pl-3 text-text-dim truncate max-w-[260px]" title={e.notes ?? ""}>{e.notes ?? ""}</td>
                                </tr>
                              );
                            })}
                            <tr className="border-t border-line/60 font-semibold">
                              <td className="py-1 pr-3 text-text-muted" colSpan={2}>{r.days.length} line{r.days.length === 1 ? "" : "s"}</td>
                              <td className="py-1 px-3 text-right font-mono">{r.total_hours.toFixed(2)}</td>
                              <td className="py-1 px-3 text-right font-mono">{r.ordinary_hours.toFixed(2)}</td>
                              <td className="py-1 px-3 text-right font-mono text-warn">{r.ot_1_5x_hours.toFixed(2)}</td>
                              <td className="py-1 px-3 text-right font-mono text-danger">{r.ot_2x_hours.toFixed(2)}</td>
                              <td className="py-1 pl-3 text-text-dim">{r.leave_hours > 0 ? `+ ${r.leave_hours.toFixed(2)} h leave` : ""}</td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </>
          )}
        </div>
      </Card>

      {/* T-467 — payroll history: recorded pay runs by fortnight, newest first */}
      <Card className="mt-4">
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Banknote size={14} className="text-text-muted" />
            <span className="text-[13px] font-medium">Payroll history</span>
            <span className="text-[11px] text-text-dim">— recorded pay runs, newest first. Click a fortnight to load it above.</span>
          </div>
          {historyByFortnight.length === 0 ? (
            <p className="text-[12px] text-text-muted">No pay runs recorded yet. Record one above after posting in Xero.</p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-text-muted border-b border-line">
                  <th className="text-left  py-2 pr-3">Fortnight</th>
                  <th className="text-left  py-2 px-3">Groups recorded</th>
                  <th className="text-right py-2 px-3 font-mono">Wages</th>
                  <th className="text-right py-2 px-3 font-mono">Super</th>
                  <th className="text-right py-2 pl-3 font-mono">Company cost</th>
                </tr>
              </thead>
              <tbody>
                {historyByFortnight.map(h => {
                  const isCurrent = h.start === start;
                  return (
                    <tr
                      key={h.start}
                      onClick={() => { setFortnight(parseISO(h.start)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      className={`border-b border-line/40 cursor-pointer hover:bg-ink-hover ${isCurrent ? "bg-accent/5" : ""}`}
                      title="Load this fortnight into the picker above"
                    >
                      <td className="py-2 pr-3 text-text">
                        {h.label}
                        {isCurrent && <span className="ml-2 text-[10px] text-accent">● loaded</span>}
                      </td>
                      <td className="py-2 px-3 text-text-muted text-[12px]">{h.groups.join(", ") || "—"}</td>
                      <td className="py-2 px-3 text-right font-mono text-text-muted">{fmtMoney(h.wages)}</td>
                      <td className="py-2 px-3 text-right font-mono text-text-muted">{fmtMoney(h.zuper)}</td>
                      <td className="py-2 pl-3 text-right font-mono text-accent font-semibold">{fmtMoney(h.cost)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number | string; tone?: "warn" | "danger" }) {
  const colour = tone === "danger" ? "text-danger" : tone === "warn" ? "text-warn" : "text-text";
  return (
    <Card>
      <div className="p-4">
        <div className="text-[10px] uppercase tracking-wider text-text-muted font-mono">{label}</div>
        <div className={`mt-1 text-[18px] font-semibold font-mono ${colour}`}>{value}</div>
      </div>
    </Card>
  );
}


// =============================================================================
// PayRunRecorder — T-463 (reworked). Inline on the Payroll page: after posting
// a run in Xero, paste the run totals here per pay group (Field / Directors /
// Casual). Company cost = Wages + Super, feeds the Commercial payroll figure.
// No per-employee entry — just what the company spent.
// =============================================================================
const PAY_GROUPS = ["Field staff", "Directors", "Casual", "Other"];

function PayRunRecorder({ start, label }: { start: string; label: string | null }) {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const [group, setGroup]   = useState("Field staff");
  const [paidDate, setPaid] = useState("");
  const [wages, setWages]   = useState("");
  const [tax, setTax]       = useState("");
  const [zuper, setZuper]   = useState("");
  const [net, setNet]       = useState("");
  const [err, setErr]       = useState<string | null>(null);

  const { data: runs = [] } = useQuery({
    queryKey: ["pay_runs_fortnight", start],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pay_runs")
        .select("pay_run_id, pay_group, paid_date, total_gross, total_super, total_paye, total_net, period:period_id!inner(period_start)")
        .eq("period.period_start", start)
        .order("paid_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const cost = (r: any) => Number(r.total_gross ?? 0) + Number(r.total_super ?? 0);
  const formCost = (Number(wages) || 0) + (Number(zuper) || 0);
  const periodTotal = runs.reduce((a: number, r: any) => a + cost(r), 0);

  const save = useMutation({
    mutationFn: async () => {
      if (!paidDate) throw new Error("Enter the payment date.");
      if (!wages && !zuper) throw new Error("Enter at least Wages and Super.");
      const { data: periodId, error: pe } = await supabase.schema("app").rpc("ensure_timesheet_period", { p_date: start });
      if (pe) throw pe;
      const num = (v: string) => (v.trim() === "" ? null : Number(v));
      const { error } = await supabase.from("pay_runs").insert({
        period_id: periodId as string,
        pay_group: group,
        paid_date: paidDate,
        total_gross: num(wages),
        total_paye:  num(tax),
        total_super: num(zuper),
        total_net:   num(net),
        status: "paid",
        prepared_by_user_id: (user as any)?.user_id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setWages(""); setTax(""); setZuper(""); setNet(""); setErr(null);
      qc.invalidateQueries({ queryKey: ["pay_runs_fortnight", start] });
      qc.invalidateQueries({ queryKey: ["commercial_dashboard"] });
    },
    onError: (e: any) => setErr(e?.message ?? "Could not record the run."),
  });

  return (
    <Card className="mb-4 border-accent/25">
      <div className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <Banknote size={14} className="text-accent" />
          <span className="text-[13px] font-medium">Record pay run{label ? ` — ${label}` : ""}</span>
        </div>
        <p className="text-[12px] text-text-muted mb-3">After you post in Xero, paste the run totals here per pay group. Company cost = Wages + Super — feeds the Commercial payroll figure. No per-employee entry needed.</p>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wide text-text-muted">Pay group</span>
            <Select value={group} onChange={e => setGroup(e.target.value)} className="mt-1">
              {PAY_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
            </Select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wide text-text-muted">Payment date</span>
            <Input type="date" value={paidDate} onChange={e => setPaid(e.target.value)} className="mt-1" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wide text-text-muted">Wages</span>
            <Input type="number" value={wages} onChange={e => setWages(e.target.value)} placeholder="gross" className="mt-1" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wide text-text-muted">Tax</span>
            <Input type="number" value={tax} onChange={e => setTax(e.target.value)} className="mt-1" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wide text-text-muted">Super</span>
            <Input type="number" value={zuper} onChange={e => setZuper(e.target.value)} className="mt-1" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wide text-text-muted">Net</span>
            <Input type="number" value={net} onChange={e => setNet(e.target.value)} className="mt-1" />
          </label>
        </div>
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <span className="text-[12px] text-text-muted">Company cost (Wages + Super): <span className="font-mono text-accent font-semibold">{fmtMoney(formCost)}</span></span>
          <Button icon={Banknote} disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Saving…" : "Record run"}</Button>
        </div>
        {err && <div className="text-[12px] text-danger mt-2">{err}</div>}

        {runs.length > 0 && (
          <div className="mt-4 border-t border-line pt-3">
            <div className="text-[11px] uppercase tracking-wide text-text-muted mb-2">Recorded this fortnight</div>
            <div className="space-y-1">
              {runs.map((r: any) => (
                <div key={r.pay_run_id} className="flex items-center justify-between text-[12px] flex-wrap gap-1">
                  <span className="text-text">{r.pay_group ?? "—"} <span className="text-text-dim">· {r.paid_date ? fmtDate(r.paid_date) : ""}</span></span>
                  <span className="font-mono text-text-muted">Wages {fmtMoney(r.total_gross)} · Super {fmtMoney(r.total_super)} · <span className="text-accent font-semibold">cost {fmtMoney(cost(r))}</span></span>
                </div>
              ))}
              <div className="flex items-center justify-between text-[12px] pt-1 border-t border-line/60 mt-1">
                <span className="font-semibold">Fortnight company cost</span>
                <span className="font-mono text-accent font-semibold">{fmtMoney(periodTotal)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
